// The page a report is about travels in the Feedback link's query string, not
// in router state: the leave guard on unsaved forms re-navigates by URL alone,
// and a query string also survives a reload.

/**
 * The originating page named by a /feedback URL's query string, or '' when
 * there is none or it is not a path on this site. The value can be set by
 * anyone who sends a link, and "Back to where you were" navigates to it, so
 * it is resolved as a URL and must stay on this origin. Pattern checks alone
 * are not enough: browsers strip tabs and line breaks before parsing, so
 * `/<tab>/evil.example` passes a "single leading slash" test and still
 * resolves to another host. Backslashes and control characters are refused
 * outright for the same reason.
 *
 * @param {string} search - location.search of the /feedback page.
 * @param {string} [origin] - This site's origin (defaults to the window's).
 * @returns {string}
 */
export function pageFromSearch(search, origin = window.location.origin) {
    const from = new URLSearchParams(search).get('from') || '';
    // eslint-disable-next-line no-control-regex
    if (!from.startsWith('/') || /[\u0000-\u001f\u007f\\]/.test(from)) {
        return '';
    }
    try {
        const url = new URL(from, origin);
        if (url.origin !== origin) return '';
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return '';
    }
}

/**
 * Where a Feedback link should go from the current location. On the form
 * itself the page the person originally came from is kept.
 *
 * @param {{ pathname: string, search: string }} location
 * @returns {string}
 */
export function feedbackLinkFor(location) {
    const from =
        location.pathname === '/feedback'
            ? pageFromSearch(location.search)
            : `${location.pathname}${location.search}`;
    return from ? `/feedback?from=${encodeURIComponent(from)}` : '/feedback';
}
